import { useRef, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { Storefront, FunnelNode, StepVariant } from '../types';
import { transformStepsToAPI } from './useFunnelData';
import { trpc } from '@/lib/api/trpc';

/**
 * Step types that require a design (instanceId/variant) to be configured
 */
// Must match what the server's checkPageConfig validates, or a step passes here
// and is rejected at save time with nothing on screen explaining why — the
// divergence TAG-1946 was about. The server skips only `external` and the two
// storefront types, so `error` belongs here too.
const STEP_TYPES_REQUIRING_DESIGN = ['checkout', 'thankyou', 'offer', 'landing', 'custom', 'error'] as const;

/**
 * Represents a step that is missing its design configuration
 */
export interface StepMissingDesign {
  id: string;
  name: string;
  type: string;
  variantId?: string; // If it's a variant within a split step
  variantLabel?: string;
}

/**
 * Check if a step type requires a design to be configured
 */
function stepRequiresDesign(node: FunnelNode): boolean {
  // Skip condition nodes (they don't have designs)
  if (node.kind === 'condition') return false;

  // Skip external storefronts (Shopify, WooCommerce) - they don't need our design
  if (node.type === 'storefront_shopify' || node.type === 'storefront_woocommerce') return false;

  // Skip external URLs
  if (node.type === 'external') return false;

  // Check if it's a step type that needs design
  return STEP_TYPES_REQUIRING_DESIGN.includes(node.type as any);
}

/**
 * Check if a step config has a valid design selected.
 *
 * Mirrors the fields of the server's `isStepConfigComplete`
 * (src/app/useCases/funnels/utils/funnelValidationRules.ts), so it can never
 * newly block a step the server would allow, nor show "Active" for one the
 * server would already block (both feed the same `saveBlocked` gate). In
 * particular it accepts the legacy v1 `config.path` alone — steps built by the
 * funnel creation wizard carry only that field.
 *
 * The predicate alone is not the whole answer: `stepRequiresDesign` above is the
 * gate deciding which nodes reach it, and its type list has to track the
 * server's skip list for the two to actually agree.
 */
function hasValidDesign(config: FunnelNode['config']): boolean {
  if (!config) return false;
  const hasPath = config.path || config.pagePath;
  const hasUrl = config.url;
  const hasInternalPath = config.metadata?.internalPath;
  const hasDeployedInstance = config.instanceId && hasInternalPath;
  const hasMountPointAndPage = config.mountPoint || config.mountPointId;
  return Boolean(hasUrl || hasPath || hasDeployedInstance || (hasMountPointAndPage && (hasPath || hasInternalPath)));
}

/**
 * Find all steps that require a design but don't have one configured
 */
export function findStepsMissingDesign(nodes: FunnelNode[] | undefined): StepMissingDesign[] {
  if (!nodes || nodes.length === 0) return [];

  const missing: StepMissingDesign[] = [];

  for (const node of nodes) {
    if (!stepRequiresDesign(node)) continue;

    // Check if it's a split step with variants
    if (node.variants && node.variants.length > 0) {
      for (const variant of node.variants) {
        if (!hasValidDesign(variant.config)) {
          missing.push({
            id: node.id,
            name: node.name,
            type: node.type,
            variantId: variant.id,
            variantLabel: variant.label,
          });
        }
      }
    } else {
      // Standard step (no variants)
      if (!hasValidDesign(node.config)) {
        missing.push({
          id: node.id,
          name: node.name,
          type: node.type,
        });
      }
    }
  }

  return missing;
}

interface UseFunnelSaveProps {
  funnel: Storefront;
  funnelId: string | undefined;
  storeId: string | undefined;
  updateWithRoutingMutation: any;
  updateFunnelFromResult: (result: any) => void;
  setConsoleResult: (result: any) => void;
  refetch: () => void;
  // Note: staticResources and scripts are now stored in stepConfig (per node/variant), not in metadata
}

/**
 * Custom hook for saving funnel with routing operations
 * Handles save logic, error parsing, and console output
 */
export function useFunnelSave({
  funnel,
  funnelId,
  storeId,
  updateWithRoutingMutation,
  updateFunnelFromResult,
  setConsoleResult,
  refetch,
}: UseFunnelSaveProps) {
  const utils = trpc.useUtils();

  // Use ref to always have latest funnel (avoids stale closure issue)
  const funnelRef = useRef(funnel);

  // Keep ref in sync with funnel
  useEffect(() => {
    funnelRef.current = funnel;
  }, [funnel]);

  // Compute steps missing design (reactive)
  const stepsMissingDesign = useMemo(() => {
    return findStepsMissingDesign(funnel.nodes);
  }, [funnel.nodes]);

  // Returns true when the save (validation + routing mutation) fully succeeded,
  // false otherwise — callers like the Publish button gate the promote dialog
  // on this instead of assuming success (errors are surfaced here via toasts
  // and the console, never thrown).
  const handleSaveFunnel = async (environment?: 'staging' | 'production'): Promise<boolean> => {
    if (!storeId || !funnelId) {
      toast.error('No funnel ID available');
      return false;
    }

    // 🛡️ BLOCK SAVING IN PRODUCTION MODE
    if (environment === 'production') {
      toast.error('Cannot edit production directly. Switch to Staging tab to make changes, then promote.');
      return false;
    }

    // 🎨 VALIDATE: All steps requiring a design must have one configured
    const currentFunnelForValidation = funnelRef.current;
    const missingDesigns = findStepsMissingDesign(currentFunnelForValidation.nodes);

    if (missingDesigns.length > 0) {
      const stepNames = missingDesigns.map((s) => {
        if (s.variantLabel) {
          return `"${s.name}" (variant: ${s.variantLabel})`;
        }
        return `"${s.name}"`;
      });

      const errorMessage =
        missingDesigns.length === 1
          ? `Step ${stepNames[0]} is missing a design. Please select a design before saving.`
          : `${missingDesigns.length} steps are missing designs: ${stepNames.join(', ')}`;

      toast.error(errorMessage, { duration: 5000 });

      // Show detailed errors in console
      setConsoleResult({
        success: false,
        errors: missingDesigns.map((s) => ({
          message: s.variantLabel
            ? `Step "${s.name}" (${s.type}) - variant "${s.variantLabel}" has no design selected`
            : `Step "${s.name}" (${s.type}) has no design selected`,
          stepId: s.id,
        })),
      });

      return false;
    }

    try {
      const currentFunnel = funnelRef.current;
      const stepsToTransform = Array.isArray(currentFunnel.steps) ? currentFunnel.steps : [];
      const transformedSteps = transformStepsToAPI(stepsToTransform);

      // Strip incomplete pixel/tracking entries to prevent validation errors
      const cleanStepConfig = (cfg: any) => {
        if (!cfg?.stepConfig?.pixels) return cfg;
        const cleaned: Record<string, any> = {};
        for (const [provider, val] of Object.entries(cfg.stepConfig.pixels)) {
          if (Array.isArray(val)) {
            const filtered = val.filter(
              (entry: any) => entry?.pixelId?.trim() || entry?.containerId?.trim() || entry?.projectId?.trim(),
            );
            if (filtered.length > 0) cleaned[provider] = filtered;
          } else if (
            val &&
            typeof val === 'object' &&
            ((val as any).pixelId?.trim() || (val as any).containerId?.trim() || (val as any).projectId?.trim())
          ) {
            cleaned[provider] = val;
          }
        }
        return {
          ...cfg,
          stepConfig: {
            ...cfg.stepConfig,
            pixels: Object.keys(cleaned).length > 0 ? cleaned : undefined,
          },
        };
      };

      const cleanedNodes = (currentFunnel.nodes || []).map((node: any) => {
        const cleanedNode = { ...node, config: cleanStepConfig(node.config) };
        if (cleanedNode.variants) {
          cleanedNode.variants = cleanedNode.variants.map((v: any) => ({
            ...v,
            config: cleanStepConfig(v.config),
          }));
        }
        return cleanedNode;
      });

      const cleanedSteps = transformedSteps.map((step: any) => {
        const cleanedStep = { ...step, config: cleanStepConfig(step.config) };
        if (cleanedStep.variants) {
          cleanedStep.variants = cleanedStep.variants.map((v: any) => ({
            ...v,
            config: cleanStepConfig(v.config),
          }));
        }
        return cleanedStep;
      });

      const funnelConfig = {
        id: currentFunnel.slug,
        name: currentFunnel.name,
        description: '',
        version: '1.0.0',
        steps: cleanedSteps,
        nodes: cleanedNodes,
        edges: currentFunnel.edges || [],
        metadata: {},
      };

      // ✅ BLOCKING pre-save validation (F07: validation bloquante + liens conflits).
      // Run the SAME validateFunnel the server enforces, but BEFORE the mutation,
      // so structural errors (duplicate paths, missing entry step, incomplete
      // page config) surface inline with "go to step" links (issues carry stepId)
      // instead of as a post-save toast. Non-fatal if the query itself fails —
      // updateWithRouting re-validates server-side, so we just fall through.
      let preValidation: { valid: boolean; issues: Array<{ type: string; message: string; stepId?: string }> } | undefined;
      try {
        preValidation = await utils.funnels.validate.fetch({ config: funnelConfig as any });
      } catch (validationError) {
        console.warn('[useFunnelSave] pre-save validation unavailable, deferring to server:', validationError);
      }

      // Non-blocking warnings (e.g. checkout without a Payment Success edge,
      // TAG-1084) — surfaced in the console after a successful save.
      const validationWarnings = (preValidation?.issues || [])
        .filter((i) => i.type === 'warning')
        .map((i) => {
          const node = (currentFunnel.nodes || []).find((n: any) => n.id === i.stepId);
          return { type: 'warning', message: i.message, stepId: i.stepId, stepName: node?.name };
        });

      if (preValidation && !preValidation.valid) {
        const validationErrors = preValidation.issues.filter((i) => i.type === 'error');
        if (validationErrors.length > 0) {
          setConsoleResult({
            success: false,
            errors: validationErrors.map((i) => {
              // Attach the step name so the console shows a "click to jump" link.
              const node = (currentFunnel.nodes || []).find((n: any) => n.id === i.stepId);
              return { message: i.message, stepId: i.stepId, stepName: node?.name };
            }),
          });
          toast.error(
            validationErrors.length === 1
              ? validationErrors[0].message
              : `${validationErrors.length} validation errors — see details below`,
            { duration: 5000 },
          );
          return false;
        }
      }

      const result = await updateWithRoutingMutation.mutateAsync({
        funnelId: funnelId,
        storeId: storeId,
        config: funnelConfig,
      });

      // Build success message from routing operations
      const { summary } = result;
      const operations = [];
      if (summary.mounted > 0) operations.push(`${summary.mounted} mounted`);
      if (summary.swapped > 0) operations.push(`${summary.swapped} swapped`);
      if (summary.unmounted > 0) operations.push(`${summary.unmounted} unmounted`);
      if (summary.split > 0)
        operations.push(`${summary.split} A/B test${summary.split > 1 ? 's' : ''} configured`);
      if (summary.migrated > 0) operations.push(`${summary.migrated} migrated`);
      if (summary.failed > 0) operations.push(`${summary.failed} failed`);

      const message =
        operations.length > 0 ? `Funnel saved! ${operations.join(', ')}` : 'Funnel saved successfully';

      toast.success(message);

      if (validationWarnings.length > 0) {
        toast(
          validationWarnings.length === 1
            ? validationWarnings[0].message
            : `${validationWarnings.length} warnings — see details in the console`,
          { icon: '⚠️', duration: 6000 },
        );
      }

      // Update funnel with new mount point IDs from result
      if (result.funnel?.config) {
        updateFunnelFromResult(result.funnel);
      }

      // Show console for operations
      setConsoleResult({
        success: true,
        operations: result.operations,
        summary: result.summary,
        warnings: validationWarnings.length > 0 ? validationWarnings : undefined,
      });

      refetch();
      return true;
    } catch (error: any) {
      console.error('Failed to save funnel:', error);

      // Extract and parse validation errors
      const errorMessage =
        error?.message || error?.data?.message || error?.shape?.message || 'Failed to save funnel';

      // Parse validation errors from numbered list format
      const errors: Array<{ message: string }> = [];
      if (errorMessage.includes('Funnel validation failed:')) {
        const errorBody = errorMessage.replace('Funnel validation failed:', '').trim();

        // Check if it's a numbered list (multiple errors)
        if (errorBody.match(/^\d+\./)) {
          const errorLines = errorBody.split('\n').filter((line: string) => line.trim());
          errorLines.forEach((line: string) => {
            const match = line.match(/^\d+\.\s*(.+)$/);
            if (match) {
              errors.push({ message: match[1].trim() });
            }
          });
        } else {
          // Single error
          errors.push({ message: errorBody });
        }
      } else {
        errors.push({ message: errorMessage });
      }

      // Show console with errors
      setConsoleResult({
        success: false,
        errors: errors,
        rawError: error?.stack || JSON.stringify(error, null, 2),
      });

      // Also show toast for immediate feedback
      toast.error(errors.length === 1 ? errors[0].message : `${errors.length} validation errors`);
      return false;
    }
  };

  return {
    handleSaveFunnel,
    isSaving: updateWithRoutingMutation.isPending,
    stepsMissingDesign,
  };
}

